<template>
  <div class="chatgpt-layout">
    <!-- Sidebar -->
    <aside class="sidebar" :class="{ collapsed: sidebarCollapsed }">
      <div class="sidebar-header">
        <el-button
          v-if="!sidebarCollapsed"
          class="new-chat-btn"
          @click="startNewChat"
        >
          <i class="el-icon-plus"></i>
          New chat
        </el-button>
        <el-button
          v-else
          icon="el-icon-plus"
          circle
          @click="startNewChat"
        ></el-button>
      </div>

      <div class="chat-history" v-if="!sidebarCollapsed">
        <div class="history-section">
          <div class="history-title">Today</div>
          <div
            v-for="(chat, idx) in chatHistory"
            :key="idx"
            class="history-item"
            :class="{ active: currentChatIndex === idx }"
            @click="loadChat(idx)"
          >
            <i class="el-icon-chat-line-round"></i>
            <span>{{ chat.title }}</span>
          </div>
        </div>
      </div>

      <div class="sidebar-footer">
        <el-dropdown trigger="click" @command="handleCommand">
          <div class="user-menu">
            <div class="user-avatar">
              <i class="el-icon-user-solid"></i>
            </div>
            <span v-if="!sidebarCollapsed" class="user-name">User</span>
            <i v-if="!sidebarCollapsed" class="el-icon-arrow-down"></i>
          </div>
          <el-dropdown-menu slot="dropdown">
            <el-dropdown-item command="profile">Profile</el-dropdown-item>
            <el-dropdown-item command="settings">Settings</el-dropdown-item>
            <el-dropdown-item command="upgrade" divided
              >Upgrade plan</el-dropdown-item
            >
            <el-dropdown-item command="logout">Log out</el-dropdown-item>
          </el-dropdown-menu>
        </el-dropdown>
      </div>
    </aside>

    <!-- Main Content -->
    <main class="main-content">
      <!-- Top Bar -->
      <header class="top-bar">
        <el-button
          class="collapse-btn"
          :icon="sidebarCollapsed ? 'el-icon-s-unfold' : 'el-icon-s-fold'"
          @click="sidebarCollapsed = !sidebarCollapsed"
        ></el-button>

        <div
          class="model-selector"
          @click="showModelDropdown = !showModelDropdown"
        >
          <span class="model-name">{{ currentModel }}</span>
          <i class="el-icon-arrow-down"></i>

          <transition name="dropdown">
            <div v-if="showModelDropdown" class="model-dropdown">
              <div class="dropdown-header">Select a model</div>
              <div
                class="dropdown-item"
                @click.stop="selectModel('gpt-5-mini')"
              >
                <div class="model-info">
                  <span class="model-title">GPT-5 Mini</span>
                  <span class="model-desc">Fast & efficient</span>
                </div>
              </div>
              <div class="dropdown-item" @click.stop="selectModel('gpt-4')">
                <div class="model-info">
                  <span class="model-title">GPT-4</span>
                  <span class="model-desc">Most capable</span>
                </div>
              </div>
              <div class="dropdown-item" @click.stop="selectModel('o1')">
                <div class="model-info">
                  <span class="model-title">o1</span>
                  <span class="model-desc">Reasoning model</span>
                  <el-tag size="mini" type="warning">Pro</el-tag>
                </div>
              </div>
              <div class="dropdown-item" @click.stop="selectModel('o3-mini')">
                <div class="model-info">
                  <span class="model-title">o3-mini</span>
                  <span class="model-desc">Fast reasoning</span>
                </div>
              </div>
            </div>
          </transition>
        </div>

        <div class="settings-toggles">
          <el-tooltip content="Thinking Mode" placement="bottom">
            <el-switch
              v-model="settings.thinkingEnabled"
              size="small"
            ></el-switch>
          </el-tooltip>
          <el-tooltip content="Reasoning Effort" placement="bottom">
            <el-select
              v-model="settings.reasoningEffort"
              size="mini"
              style="width: 90px; margin-left: 8px"
            >
              <el-option label="Low" value="low"></el-option>
              <el-option label="Medium" value="medium"></el-option>
              <el-option label="High" value="high"></el-option>
            </el-select>
          </el-tooltip>
        </div>
      </header>

      <!-- Chat Area -->
      <div class="chat-area" ref="chatArea">
        <!-- Welcome Screen -->
        <div v-if="!currentMessages.length" class="welcome-screen">
          <div class="welcome-icon">
            <div class="icon-circle">
              <svg width="36" height="36" viewBox="0 0 41 41" fill="none">
                <path
                  d="M37.5324 16.8707C37.9808 15.5241 38.1363 14.0974 37.9886 12.6859C37.8409 11.2744 37.3936 9.91076 36.676 8.68622C35.6126 6.83404 33.9882 5.3676 32.0373 4.4985C30.0864 3.62941 27.9098 3.40259 25.8215 3.85078C24.8796 2.7893 23.7219 122.4257 1.363.94125 41C21.1295 0.785575 19.7249 0.491269 18.3058 0.500197C16.1708 0.495044 14.0893 1.16803 12.3614 2.42287C10.6336 3.6777 9.34854 5.44687 8.68622 7.41951C7.19299 7.37171 5.70567 7.64185 4.32415 8.2116C2.94263 8.78135 1.69954 9.63706 0.676048 10.7207C-0.795205 12.6056 -0.970876 15.1577 0.218043 17.2183C1.40696 19.279 3.59171 20.5817 5.96767 20.7473C5.51932 22.0939 5.36378 23.5206 5.51147 24.9321C5.65916 26.3436 6.10647 27.7072 6.82404 28.9318C7.88744 30.784 9.51181 32.2504 11.4627 33.1195C13.4136 33.9886 15.5902 34.2154 17.6785 33.7672C18.6204 34.8287 19.7781 35.6768 21.0743 36.2546C22.3705 36.8324 23.7751 37.1267 25.1942 37.1178C27.3292 37.1229 29.4107 36.45 31.1386 35.1952C32.8664 33.9403 34.1514 32.1711 34.8137 30.1985C36.3069 30.2463 37.7943 29.9761 39.1758 29.4064C40.5573 28.8366 41.8004 27.9809 42.8239 26.8973C44.2951 25.0123 44.4708 22.4602 43.2818 20.3996C42.0929 18.3389 39.9082 17.0362 37.5324 16.8707ZM22.4978 30.0993V36.6405C22.4978 37.3015 21.9734 37.8258 21.3124 37.8258C20.6514 37.8258 20.127 37.3015 20.127 36.6405V30.0993C20.127 29.4383 20.6514 28.9139 21.3124 28.9139C21.9734 28.9139 22.4978 29.4383 22.4978 30.0993ZM30.6843 36.6405V30.0993C30.6843 29.4383 31.2087 28.9139 31.8697 28.9139C32.5307 28.9139 33.0551 29.4383 33.0551 30.0993V36.6405C33.0551 37.3015 32.5307 37.8258 31.8697 37.8258C31.2087 37.8258 30.6843 37.3015 30.6843 36.6405ZM12.3124 33.4999V28.9139C12.3124 28.2529 12.8368 27.7285 13.4978 27.7285C14.1588 27.7285 14.6832 28.2529 14.6832 28.9139V33.4999C14.6832 34.1609 14.1588 34.6853 13.4978 34.6853C12.8368 34.6853 12.3124 34.1609 12.3124 33.4999ZM21.3124 18.5562C20.6514 18.5562 20.127 18.0318 20.127 17.3708C20.127 16.7098 20.6514 16.1854 21.3124 16.1854C21.9734 16.1854 22.4978 16.7098 22.4978 17.3708C22.4978 18.0318 21.9734 18.5562 21.3124 18.5562ZM31.8697 18.5562C31.2087 18.5562 30.6843 18.0318 30.6843 17.3708C30.6843 16.7098 31.2087 16.1854 31.8697 16.1854C32.5307 16.1854 33.0551 16.7098 33.0551 17.3708C33.0551 18.0318 32.5307 18.5562 31.8697 18.5562ZM40.5415 21.442C39.8805 21.442 39.3561 20.9176 39.3561 20.2566C39.3561 19.5956 39.8805 19.0712 40.5415 19.0712C41.2025 19.0712 41.7269 19.5956 41.7269 20.2566C41.7269 20.9176 41.2025 21.442 40.5415 21.442ZM13.4978 20.2566C12.8368 20.2566 12.3124 19.7322 12.3124 19.0712C12.3124 18.4102 12.8368 17.8858 13.4978 17.8858C14.1588 17.8858 14.6832 18.4102 14.6832 19.0712C14.6832 19.7322 14.1588 20.2566 13.4978 20.2566Z"
                  fill="white"
                />
              </svg>
            </div>
          </div>
          <h1>How can I help you today?</h1>
          <div class="suggestion-grid">
            <div
              class="suggestion-card"
              @click="
                useSuggestion('Explain quantum computing in simple terms')
              "
            >
              <div class="suggestion-header">
                <span class="suggestion-icon">
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                  >
                    <circle cx="12" cy="12" r="10" />
                    <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
                    <line x1="12" y1="17" x2="12.01" y2="17" />
                  </svg>
                </span>
                <span class="suggestion-title">Explain</span>
              </div>
              <p>Explain quantum computing in simple terms</p>
            </div>
            <div
              class="suggestion-card"
              @click="
                useSuggestion('Write a Python script to analyze sales data')
              "
            >
              <div class="suggestion-header">
                <span class="suggestion-icon">
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                  >
                    <path
                      d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"
                    />
                    <polyline points="14 2 14 8 20 8" />
                    <line x1="16" y1="13" x2="8" y2="13" />
                    <line x1="16" y1="17" x2="8" y2="17" />
                  </svg>
                </span>
                <span class="suggestion-title">Write</span>
              </div>
              <p>Write a Python script to analyze sales data</p>
            </div>
            <div
              class="suggestion-card"
              @click="
                useSuggestion(
                  'What are the best tips for improving productivity?'
                )
              "
            >
              <div class="suggestion-header">
                <span class="suggestion-icon">
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                  >
                    <polygon
                      points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"
                    />
                  </svg>
                </span>
                <span class="suggestion-title">Brainstorm</span>
              </div>
              <p>What are the best tips for improving productivity?</p>
            </div>
            <div
              class="suggestion-card"
              @click="useSuggestion('Help me debug this code')"
            >
              <div class="suggestion-header">
                <span class="suggestion-icon">
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                  >
                    <circle cx="12" cy="12" r="3" />
                    <path
                      d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"
                    />
                  </svg>
                </span>
                <span class="suggestion-title">Debug</span>
              </div>
              <p>Help me debug this code</p>
            </div>
          </div>
        </div>

        <!-- Messages -->
        <div v-else class="messages-container">
          <div
            v-for="(msg, idx) in currentMessages"
            :key="idx"
            class="message-row"
            :class="msg.role"
          >
            <div class="message-avatar">
              <div v-if="msg.role === 'assistant'" class="gpt-avatar">
                <svg width="20" height="20" viewBox="0 0 41 41" fill="none">
                  <path
                    d="M37.5324 16.8707C37.9808 15.5241 38.1363 14.0974 37.9886 12.6859C37.8409 11.2744 37.3936 9.91076 36.676 8.68622C35.6126 6.83404 33.9882 5.3676 32.0373 4.4985C30.0864 3.62941 27.9098 3.40259 25.8215 3.85078C24.8796 2.7893 23.7219 1.94125 22.4257 1.36341C21.1295 0.785575 19.7249 0.491269 18.3058 0.500197C16.1708 0.495044 14.0893 1.16803 12.3614 2.42287C10.6336 3.6777 9.34854 5.44687 8.68622 7.41951C7.19299 7.37171 5.70567 7.64185 4.32415 8.2116C2.94263 8.78135 1.69954 9.63706 0.676048 10.7207C-0.795205 12.6056 -0.970876 15.1577 0.218043 17.2183C1.40696 19.279 3.59171 20.5817 5.96767 20.7473C5.51932 22.0939 5.36378 23.5206 5.51147 24.9321C5.65916 26.3436 6.10647 27.7072 6.82404 28.9318C7.88744 30.784 9.51181 32.2504 11.4627 33.1195C13.4136 33.9886 15.5902 34.2154 17.6785 33.7672C18.6204 34.8287 19.7781 35.6768 21.0743 36.2546C22.3705 36.8324 23.7751 37.1267 25.1942 37.1178C27.3292 37.1229 29.4107 36.45 31.1386 35.1952C32.8664 33.9403 34.1514 32.1711 34.8137 30.1985C36.3069 30.2463 37.7943 29.9761 39.1758 29.4064C40.5573 28.8366 41.8004 27.9809 42.8239 26.8973C44.2951 25.0123 44.4708 22.4602 43.2818 20.3996C42.0929 18.3389 39.9082 17.0362 37.5324 16.8707ZM22.4978 30.0993V36.6405C22.4978 37.3015 21.9734 37.8258 21.3124 37.8258C20.6514 37.8258 20.127 37.3015 20.127 36.6405V30.0993C20.127 29.4383 20.6514 28.9139 21.3124 28.9139C21.9734 28.9139 22.4978 29.4383 22.4978 30.0993ZM30.6843 36.6405V30.0993C30.6843 29.4383 31.2087 28.9139 31.8697 28.9139C32.5307 28.9139 33.0551 29.4383 33.0551 30.0993V36.6405C33.0551 37.3015 32.5307 37.8258 31.8697 37.8258C31.2087 37.8258 30.6843 37.3015 30.6843 36.6405ZM12.3124 33.4999V28.9139C12.3124 28.2529 12.8368 27.7285 13.4978 27.7285C14.1588 27.7285 14.6832 28.2529 14.6832 28.9139V33.4999C14.6832 34.1609 14.1588 34.6853 13.4978 34.6853C12.8368 34.6853 12.3124 34.1609 12.3124 33.4999ZM21.3124 18.5562C20.6514 18.5562 20.127 18.0318 20.127 17.3708C20.127 16.7098 20.6514 16.1854 21.3124 16.1854C21.9734 16.1854 22.4978 16.7098 22.4978 17.3708C22.4978 18.0318 21.9734 18.5562 21.3124 18.5562ZM31.8697 18.5562C31.2087 18.5562 30.6843 18.0318 30.6843 17.3708C30.6843 16.7098 31.2087 16.1854 31.8697 16.1854C32.5307 16.1854 33.0551 16.7098 33.0551 17.3708C33.0551 18.0318 32.5307 18.5562 31.8697 18.5562ZM40.5415 21.442C39.8805 21.442 39.3561 20.9176 39.3561 20.2566C39.3561 19.5956 39.8805 19.0712 40.5415 19.0712C41.2025 19.0712 41.7269 19.5956 41.7269 20.2566C41.7269 20.9176 41.2025 21.442 40.5415 21.442ZM13.4978 20.2566C12.8368 20.2566 12.3124 19.7322 12.3124 19.0712C12.3124 18.4102 12.8368 17.8858 13.4978 17.8858C14.1588 17.8858 14.6832 18.4102 14.6832 19.0712C14.6832 19.7322 14.1588 20.2566 13.4978 20.2566Z"
                    fill="white"
                  />
                </svg>
              </div>
              <div v-else class="user-avatar">
                <i class="el-icon-user"></i>
              </div>
            </div>
            <div class="message-content">
              <div class="message-role">
                {{ msg.role === "assistant" ? "ChatGPT" : "You" }}
              </div>
              <!-- Thinking Section -->
              <div v-if="msg.thinking" class="thinking-section">
                <div
                  class="thinking-header"
                  @click="msg.showThinking = !msg.showThinking"
                >
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                  >
                    <circle cx="12" cy="12" r="10" />
                    <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
                    <line x1="12" y1="17" x2="12.01" y2="17" />
                  </svg>
                  <span>Thinking...</span>
                  <i
                    :class="
                      msg.showThinking
                        ? 'el-icon-arrow-up'
                        : 'el-icon-arrow-down'
                    "
                    class="thinking-toggle"
                  ></i>
                </div>
                <el-collapse-transition>
                  <div v-show="msg.showThinking" class="thinking-content">
                    <pre>{{ msg.thinking }}</pre>
                  </div>
                </el-collapse-transition>
              </div>
              <!-- Main Response -->
              <div
                class="message-text"
                v-html="renderMarkdown(msg.content)"
              ></div>
            </div>
          </div>

          <!-- Typing Indicator -->
          <div v-if="isTyping" class="message-row assistant">
            <div class="message-avatar">
              <div class="gpt-avatar">
                <svg width="20" height="20" viewBox="0 0 41 41" fill="none">
                  <path
                    d="M37.5324 16.8707C37.9808 15.5241 38.1363 14.0974 37.9886 12.6859C37.8409 11.2744 37.3936 9.91076 36.676 8.68622C35.6126 6.83404 33.9882 5.3676 32.0373 4.4985C30.0864 3.62941 27.9098 3.40259 25.8215 3.85078C24.8796 2.7893 23.7219 1.94125 22.4257 1.36341C21.1295 0.785575 19.7249 0.491269 18.3058 0.500197C16.1708 0.495044 14.0893 1.16803 12.3614 2.42287C10.6336 3.6777 9.34854 5.44687 8.68622 7.41951C7.19299 7.37171 5.70567 7.64185 4.32415 8.2116C2.94263 8.78135 1.69954 9.63706 0.676048 10.7207C-0.795205 12.6056 -0.970876 15.1577 0.218043 17.2183C1.40696 19.279 3.59171 20.5817 5.96767 20.7473C5.51932 22.0939 5.36378 23.5206 5.51147 24.9321C5.65916 26.3436 6.10647 27.7072 6.82404 28.9318C7.88744 30.784 9.51181 32.2504 11.4627 33.1195C13.4136 33.9886 15.5902 34.2154 17.6785 33.7672C18.6204 34.8287 19.7781 35.6768 21.0743 36.2546C22.3705 36.8324 23.7751 37.1267 25.1942 37.1178C27.3292 37.1229 29.4107 36.45 31.1386 35.1952C32.8664 33.9403 34.1514 32.1711 34.8137 30.1985C36.3069 30.2463 37.7943 29.9761 39.1758 29.4064C40.5573 28.8366 41.8004 27.9809 42.8239 26.8973C44.2951 25.0123 44.4708 22.4602 43.2818 20.3996C42.0929 18.3389 39.9082 17.0362 37.5324 16.8707ZM22.4978 30.0993V36.6405C22.4978 37.3015 21.9734 37.8258 21.3124 37.8258C20.6514 37.8258 20.127 37.3015 20.127 36.6405V30.0993C20.127 29.4383 20.6514 28.9139 21.3124 28.9139C21.9734 28.9139 22.4978 29.4383 22.4978 30.0993ZM30.6843 36.6405V30.0993C30.6843 29.4383 31.2087 28.9139 31.8697 28.9139C32.5307 28.9139 33.0551 29.4383 33.0551 30.0993V36.6405C33.0551 37.3015 32.5307 37.8258 31.8697 37.8258C31.2087 37.8258 30.6843 37.3015 30.6843 36.6405ZM12.3124 33.4999V28.9139C12.3124 28.2529 12.8368 27.7285 13.4978 27.7285C14.1588 27.7285 14.6832 28.2529 14.6832 28.9139V33.4999C14.6832 34.1609 14.1588 34.6853 13.4978 34.6853C12.8368 34.6853 12.3124 34.1609 12.3124 33.4999ZM21.3124 18.5562C20.6514 18.5562 20.127 18.0318 20.127 17.3708C20.127 16.7098 20.6514 16.1854 21.3124 16.1854C21.9734 16.1854 22.4978 16.7098 22.4978 17.3708C22.4978 18.0318 21.9734 18.55624 18. 21.3125562ZM31.8697 18.5562C31.2087 18.5562 30.6843 18.0318 30.6843 17.3708C30.6843 16.7098 31.2087 16.1854 31.8697 16.1854C32.5307 16.1854 33.0551 16.7098 33.0551 17.3708C33.0551 18.0318 32.5307 18.5562 31.8697 18.5562ZM40.5415 21.442C39.8805 21.442 39.3561 20.9176 39.3561 20.2566C39.3561 19.5956 39.8805 19.0712 40.5415 19.0712C41.2025 19.0712 41.7269 19.5956 41.7269 20.2566C41.7269 20.9176 41.2025 21.442 40.5415 21.442ZM13.4978 20.2566C12.8368 20.2566 12.3124 19.7322 12.3124 19.0712C12.3124 18.4102 12.8368 17.8858 13.4978 17.8858C14.1588 17.8858 14.6832 18.4102 14.6832 19.0712C14.6832 19.7322 14.1588 20.2566 13.4978 20.2566Z"
                    fill="white"
                  />
                </svg>
              </div>
            </div>
            <div class="message-content">
              <div class="typing-indicator">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          </div>

          <div ref="messagesEnd"></div>
        </div>
      </div>

      <!-- Input Area -->
      <div class="input-area">
        <div class="input-container">
          <div class="input-box">
            <textarea
              ref="inputTextarea"
              v-model="userInput"
              placeholder="Message ChatGPT"
              @keydown.enter.exact.prevent="sendMessage"
              @input="autoResize"
              rows="1"
            ></textarea>
            <el-button
              class="send-btn"
              :class="{ active: userInput.trim() }"
              :disabled="!userInput.trim() || isTyping"
              @click="sendMessage"
              icon="el-icon-top"
              circle
            ></el-button>
          </div>
          <div class="input-footer">
            ChatGPT can make mistakes. Please verify important information.
          </div>
        </div>
      </div>
    </main>

    <!-- Click outside to close dropdown -->
    <div
      v-if="showModelDropdown"
      class="dropdown-backdrop"
      @click="showModelDropdown = false"
    ></div>
  </div>
</template>

<script>
import Vue from "vue";

export default {
  name: "ChatGPTPage",
  data() {
    return {
      userInput: "",
      isTyping: false,
      sidebarCollapsed: false,
      showModelDropdown: false,
      currentChatIndex: -1,
      currentModel: "gpt-5-mini",
      settings: {
        systemRole: "You are a helpful assistant.",
        maxTokens: 1024,
        thinkingEnabled: false,
        reasoningEffort: "medium",
      },
      chatHistory: [],
    };
  },
  computed: {
    currentMessages() {
      if (
        this.currentChatIndex >= 0 &&
        this.chatHistory[this.currentChatIndex]
      ) {
        return this.chatHistory[this.currentChatIndex].messages;
      }
      return [];
    },
  },
  mounted() {
    this.autoResize();
    this.loadFromStorage();
  },
  methods: {
    startNewChat() {
      this.currentChatIndex = -1;
      this.saveToStorage();
    },

    loadChat(index) {
      this.currentChatIndex = index;
    },

    useSuggestion(suggestion) {
      this.userInput = suggestion;
      this.$nextTick(() => {
        this.sendMessage();
      });
    },

    async sendMessage() {
      const content = this.userInput.trim();
      if (!content || this.isTyping) return;

      if (this.currentChatIndex < 0) {
        const newChat = {
          title: content.slice(0, 30) + (content.length > 30 ? "..." : ""),
          messages: [],
          model: this.currentModel,
          settings: { ...this.settings },
        };
        this.chatHistory.unshift(newChat);
        this.currentChatIndex = 0;
      }

      const currentChat = this.chatHistory[this.currentChatIndex];
      currentChat.messages.push({
        role: "user",
        content: content,
      });

      this.userInput = "";
      this.$nextTick(() => {
        this.autoResize();
        this.scrollToBottom();
      });

      await this.streamResponse(currentChat.messages);
      this.saveToStorage();
    },

    async streamResponse(messages) {
      const url = `${process.env.VUE_APP_BASE_API || ""}/v1/chat/stream`;

      const currentChat = this.chatHistory[this.currentChatIndex];

      const requestBody = {
        messages: messages.filter((m) => m.role !== "thinking"),
        model: currentChat.model || this.currentModel,
        max_tokens: currentChat.settings.maxTokens || this.settings.maxTokens,
        thinking_enabled:
          currentChat.settings.thinkingEnabled || this.settings.thinkingEnabled,
        reasoning_effort:
          currentChat.settings.reasoningEffort || this.settings.reasoningEffort,
      };

      try {
        const response = await fetch(url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${Vue.$cookies.get("accessToken") || ""}`,
            user_id: Vue.$cookies.get("userID") || "",
          },
          body: JSON.stringify(requestBody),
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";

        messages.push({
          role: "assistant",
          content: "",
          thinking: "",
          showThinking: false,
        });

        let assistantContent = "";
        let thinkingContent = "";

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split("\n");
          buffer = lines.pop() || "";

          for (const line of lines) {
            if (line.startsWith("data:")) {
              const dataStr = line.slice(5).trim();
              if (!dataStr) continue;

              try {
                const data = JSON.parse(dataStr);

                if (data.error) {
                  this.handleError(data.error, messages);
                  return;
                }

                if (data.status === "done") {
                  this.finishStreaming(messages);
                  return;
                }

                const lastMsg = messages[messages.length - 1];
                if (lastMsg.role === "assistant") {
                  const thinkingDelta = this.extractThinking(data);
                  if (thinkingDelta) {
                    thinkingContent += thinkingDelta;
                    lastMsg.thinking = thinkingContent;
                  }

                  const content = this.extractContent(data);
                  if (content) {
                    assistantContent += content;
                    lastMsg.content = assistantContent;
                  }
                }
                this.$nextTick(() => {
                  this.scrollToBottom();
                });
              } catch (e) {
                console.error("Parse SSE data error:", e);
              }
            }
          }
        }

        this.finishStreaming(messages);
      } catch (error) {
        console.error("Request error:", error);
        this.handleError("Failed to connect to the server.", messages);
      }
    },

    extractContent(data) {
      if (data.choices && data.choices.length > 0) {
        const delta = data.choices[0].delta;
        if (delta && delta.content) {
          return delta.content;
        }
      }
      return "";
    },

    extractThinking(data) {
      if (data.choices && data.choices.length > 0) {
        const delta = data.choices[0].delta;
        if (delta) {
          if (delta.thinking) {
            return delta.thinking;
          }
          if (delta.reasoning_content) {
            return delta.reasoning_content;
          }
        }
      }
      return "";
    },

    finishStreaming(messages) {
      this.isTyping = false;
      this.saveToStorage();
    },

    handleError(errorMessage, messages) {
      this.isTyping = false;
      messages.push({
        role: "assistant",
        content: `Error: ${errorMessage}`,
      });
      this.saveToStorage();
    },

    selectModel(model) {
      this.currentModel = model;
      this.showModelDropdown = false;
      if (this.currentChatIndex >= 0) {
        this.chatHistory[this.currentChatIndex].model = model;
        this.saveToStorage();
      }
    },

    handleCommand(command) {
      if (command === "settings") {
        this.$message.info("Settings panel coming soon");
      } else if (command === "upgrade") {
        this.$message.info("Upgrade plan coming soon");
      } else if (command === "logout") {
        this.$message.info("Logout coming soon");
      }
    },

    autoResize() {
      const textarea = this.$refs.inputTextarea;
      if (textarea) {
        textarea.style.height = "auto";
        textarea.style.height = Math.min(textarea.scrollHeight, 200) + "px";
      }
    },

    scrollToBottom() {
      this.$nextTick(() => {
        const end = this.$refs.messagesEnd;
        if (end) {
          end.scrollIntoView({ behavior: "smooth" });
        }
      });
    },

    renderMarkdown(content) {
      if (!content) return "";

      let html = content;

      html = html
        .replace(/```(\w+)?\n([\s\S]*?)```/g, (match, lang, code) => {
          return `<pre class="code-block"><code class="language-${
            lang || "text"
          }">${this.escapeHtml(code.trim())}</code></pre>`;
        })
        .replace(/`([^`]+)`/g, "<code>$1</code>")
        .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
        .replace(/\*([^*]+)\*/g, "<em>$1</em>")
        .replace(/^- (.+)$/gm, "<li>$1</li>")
        .replace(/(<li>.*<\/li>)+/g, "<ul>$&</ul>")
        .replace(/^### (.+)$/gm, "<h3>$1</h3>")
        .replace(/^## (.+)$/gm, "<h2>$1</h2>")
        .replace(/^# (.+)$/gm, "<h1>$1</h1>")
        .replace(/\n/g, "<br>");

      return html;
    },

    escapeHtml(text) {
      const div = document.createElement("div");
      div.textContent = text;
      return div.innerHTML;
    },

    saveToStorage() {
      try {
        localStorage.setItem("chatHistory", JSON.stringify(this.chatHistory));
        localStorage.setItem("currentChatIndex", this.currentChatIndex);
      } catch (e) {
        console.error("Failed to save to storage:", e);
      }
    },

    loadFromStorage() {
      try {
        const saved = localStorage.getItem("chatHistory");
        if (saved) {
          this.chatHistory = JSON.parse(saved);
        }
        const savedIndex = localStorage.getItem("currentChatIndex");
        if (savedIndex) {
          this.currentChatIndex = parseInt(savedIndex);
        }
      } catch (e) {
        console.error("Failed to load from storage:", e);
      }
    },
  },
};
</script>

<style scoped>
.chatgpt-layout {
  display: flex;
  height: 100vh;
  background: #212121;
  color: #ececec;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica,
    Arial, sans-serif;
}

.sidebar {
  width: 260px;
  background: #171717;
  display: flex;
  flex-direction: column;
  border-right: 1px solid #2f2f2f;
  transition: width 0.3s ease;
}

.sidebar.collapsed {
  width: 60px;
}

.sidebar-header {
  padding: 10px;
  border-bottom: 1px solid #2f2f2f;
}

.new-chat-btn {
  width: 100%;
  justify-content: flex-start;
  background: #2f2f2f;
  border: 1px solid #2f2f2f;
  color: #ececec;
  padding: 12px 16px;
  border-radius: 8px;
  font-size: 14px;
  transition: all 0.2s;
}

.new-chat-btn:hover {
  background: #3a3a3a;
}

.chat-history {
  flex: 1;
  overflow-y: auto;
  padding: 8px;
}

.history-section {
  margin-bottom: 16px;
}

.history-title {
  font-size: 12px;
  color: #8e8ea0;
  padding: 8px 12px;
  font-weight: 500;
}

.history-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 8px;
  cursor: pointer;
  color: #ececec;
  font-size: 14px;
  transition: background 0.2s;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.history-item:hover {
  background: #2f2f2f;
}

.history-item.active {
  background: #2f2f2f;
}

.history-item i {
  font-size: 16px;
  color: #8e8ea0;
  flex-shrink: 0;
}

.sidebar-footer {
  padding: 12px;
  border-top: 1px solid #2f2f2f;
}

.user-menu {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.2s;
}

.user-menu:hover {
  background: #2f2f2f;
}

.user-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #5a5a5a;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ececec;
  font-size: 16px;
}

.user-name {
  flex: 1;
  font-size: 14px;
}

.main-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.top-bar {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid #2f2f2f;
  gap: 12px;
}

.collapse-btn {
  background: transparent;
  border: none;
  color: #8e8ea0;
  font-size: 20px;
}

.collapse-btn:hover {
  color: #ececec;
  background: #2f2f2f;
}

.model-selector {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  border-radius: 8px;
  cursor: pointer;
  position: relative;
  transition: background 0.2s;
}

.model-selector:hover {
  background: #2f2f2f;
}

.model-name {
  font-size: 14px;
  font-weight: 600;
}

.model-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  margin-top: 8px;
  width: 280px;
  background: #2f2f2f;
  border-radius: 12px;
  padding: 8px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.4);
  z-index: 100;
}

.dropdown-header {
  font-size: 12px;
  color: #8e8ea0;
  padding: 8px 12px;
  border-bottom: 1px solid #3a3a3a;
  margin-bottom: 8px;
}

.dropdown-item {
  padding: 12px;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.2s;
}

.dropdown-item:hover {
  background: #3a3a3a;
}

.model-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.model-title {
  font-size: 14px;
  font-weight: 600;
}

.model-desc {
  font-size: 12px;
  color: #8e8ea0;
}

.settings-toggles {
  display: flex;
  align-items: center;
  margin-left: auto;
}

.dropdown-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 99;
}

.chat-area {
  flex: 1;
  overflow-y: auto;
  padding-bottom: 140px;
}

.welcome-screen {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100%;
  padding: 40px 20px;
}

.welcome-icon {
  margin-bottom: 24px;
}

.icon-circle {
  width: 68px;
  height: 68px;
  border-radius: 50%;
  background: linear-gradient(135deg, #10a37f 0%, #1a7f64 100%);
  display: flex;
  align-items: center;
  justify-content: center;
}

.welcome-screen h1 {
  font-size: 28px;
  font-weight: 600;
  margin: 0 0 32px;
  text-align: center;
}

.suggestion-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  max-width: 600px;
  width: 100%;
}

.suggestion-card {
  background: #2f2f2f;
  border-radius: 12px;
  padding: 16px;
  cursor: pointer;
  transition: all 0.2s;
  border: 1px solid transparent;
}

.suggestion-card:hover {
  background: #3a3a3a;
  border-color: #5a5a5a;
}

.suggestion-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.suggestion-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #10a37f;
}

.suggestion-title {
  font-size: 14px;
  font-weight: 600;
}

.suggestion-card p {
  margin: 0;
  font-size: 13px;
  color: #b4b4b4;
  line-height: 1.4;
}

.messages-container {
  padding: 0;
}

.message-row {
  display: flex;
  gap: 16px;
  padding: 24px 24px;
  border-bottom: 1px solid #2f2f2f;
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.message-row:first-child {
  padding-top: 16px;
}

.message-row.user {
  background: #2f2f2f;
}

.message-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.gpt-avatar {
  background: linear-gradient(135deg, #10a37f 0%, #1a7f64 100%);
  width: 100%;
  height: 100%;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.message-content {
  flex: 1;
  max-width: 768px;
  margin: 0 auto;
  width: 100%;
}

.message-role {
  font-size: 13px;
  font-weight: 600;
  margin-bottom: 8px;
  color: #10a37f;
}

.message-row.user .message-role {
  color: #8e8ea0;
}

.message-text {
  font-size: 15px;
  line-height: 1.7;
  color: #ececec;
}

.message-text :deep(pre) {
  background: #1a1a1a;
  border-radius: 8px;
  padding: 16px;
  overflow-x: auto;
  margin: 12px 0;
  border: 1px solid #2f2f2f;
}

.message-text :deep(code) {
  font-family: "SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace;
  font-size: 13px;
}

.message-text :deep(code:not(pre code)) {
  background: #3a3a3a;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 13px;
}

.message-text :deep(ul) {
  margin: 12px 0;
  padding-left: 24px;
}

.message-text :deep(li) {
  margin: 4px 0;
}

.thinking-section {
  margin-bottom: 12px;
  border-radius: 8px;
  overflow: hidden;
}

.thinking-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: #3a3a3a;
  border-radius: 8px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  color: #10a37f;
  transition: background 0.2s;
}

.thinking-header:hover {
  background: #4a4a4a;
}

.thinking-toggle {
  margin-left: auto;
  color: #8e8ea0;
  font-size: 12px;
}

.thinking-content {
  background: #2a2a2a;
  border-radius: 0 0 8px 8px;
  padding: 12px;
  margin-top: 2px;
}

.thinking-content pre {
  margin: 0;
  white-space: pre-wrap;
  word-wrap: break-word;
  font-family: "SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace;
  font-size: 12px;
  line-height: 1.6;
  color: #a0a0a0;
}

.message-text :deep(h1),
.message-text :deep(h2),
.message-text :deep(h3) {
  margin: 16px 0 8px;
  font-weight: 600;
}

.message-text :deep(h1) {
  font-size: 20px;
}

.message-text :deep(h2) {
  font-size: 18px;
}

.message-text :deep(h3) {
  font-size: 16px;
}

.typing-indicator {
  display: flex;
  gap: 4px;
  padding: 8px 0;
}

.typing-indicator span {
  width: 8px;
  height: 8px;
  background: #8e8ea0;
  border-radius: 50%;
  animation: bounce 1.4s infinite ease-in-out;
}

.typing-indicator span:nth-child(1) {
  animation-delay: 0s;
}

.typing-indicator span:nth-child(2) {
  animation-delay: 0.2s;
}

.typing-indicator span:nth-child(3) {
  animation-delay: 0.4s;
}

@keyframes bounce {
  0%,
  80%,
  100% {
    transform: scale(0);
  }
  40% {
    transform: scale(1);
  }
}

.input-area {
  position: fixed;
  bottom: 0;
  left: 260px;
  right: 0;
  background: linear-gradient(transparent, #212121 20%);
  padding: 20px 24px 24px;
}

.sidebar.collapsed + .main-content .input-area {
  left: 60px;
}

.input-container {
  max-width: 768px;
  margin: 0 auto;
}

.input-box {
  background: #2f2f2f;
  border-radius: 16px;
  padding: 12px 16px;
  display: flex;
  align-items: flex-end;
  gap: 8px;
  border: 1px solid transparent;
  transition: border-color 0.2s;
}

.input-box:focus-within {
  border-color: #5a5a5a;
}

.input-box textarea {
  flex: 1;
  background: transparent;
  border: none;
  outline: none;
  color: #ececec;
  font-size: 15px;
  line-height: 1.5;
  resize: none;
  max-height: 200px;
  font-family: inherit;
}

.input-box textarea::placeholder {
  color: #8e8ea0;
}

.send-btn {
  background: #ececec;
  border: none;
  color: #212121;
  width: 32px;
  height: 32px;
  transition: all 0.2s;
}

.send-btn.active {
  background: #ececec;
  color: #212121;
}

.send-btn:disabled {
  background: #3a3a3a;
  color: #5a5a5a;
}

.input-footer {
  text-align: center;
  margin-top: 12px;
  font-size: 12px;
  color: #8e8ea0;
}

.dropdown-enter-active,
.dropdown-leave-active {
  transition: all 0.2s ease;
}

.dropdown-enter,
.dropdown-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

@media (max-width: 768px) {
  .sidebar {
    position: fixed;
    left: -260px;
    z-index: 10;
    height: 100vh;
  }

  .sidebar.collapsed {
    left: 0;
    width: 260px;
  }

  .input-area {
    left: 0;
  }

  .suggestion-grid {
    grid-template-columns: 1fr;
  }
}
</style>
